import type { PersonType } from '@/stores/models/Person';
import type {
  MembersLivingFilter,
  MembersPeopleFilters,
  MembersSortBy,
  MembersSortDirection
} from '@/types/members-list';
import { calendarYearFromStoredDate } from '@/utils/dateFormatting';

export function matchesLivingFilter(person: PersonType, living: MembersLivingFilter): boolean {
  if (living === 'all') return true;
  if (living === 'living') return person.isAlive;
  return !person.isAlive;
}

export function matchesPeopleFilters(
  person: PersonType,
  filters: MembersPeopleFilters,
  hasRelationships: (familyMemberId: string) => boolean
): boolean {
  if (!matchesLivingFilter(person, filters.living)) return false;
  if (filters.hasPhoto && !person.photoUrl) return false;
  if (filters.hasNoRelationships && hasRelationships(person.familyMemberId)) return false;
  return true;
}

function comparePeople(a: PersonType, b: PersonType, sortBy: MembersSortBy): number {
  if (sortBy === 'recent') {
    const timeA = a.updatedAt ? Date.parse(a.updatedAt) : 0;
    const timeB = b.updatedAt ? Date.parse(b.updatedAt) : 0;
    if (timeA !== timeB) return timeA - timeB;
    return (a.lastName || '').localeCompare(b.lastName || '');
  }
  if (sortBy === 'first') {
    const firstCompare = (a.firstName || '').localeCompare(b.firstName || '');
    if (firstCompare !== 0) return firstCompare;
    return (a.lastName || '').localeCompare(b.lastName || '');
  }
  if (sortBy === 'birth') {
    const yearA = calendarYearFromStoredDate(a.birthDate) ?? 9999;
    const yearB = calendarYearFromStoredDate(b.birthDate) ?? 9999;
    if (yearA !== yearB) return yearA - yearB;
    return (a.lastName || '').localeCompare(b.lastName || '');
  }
  const lastCompare = (a.lastName || '').localeCompare(b.lastName || '');
  if (lastCompare !== 0) return lastCompare;
  return (a.firstName || '').localeCompare(b.firstName || '');
}

export function sortPeople(people: PersonType[], sortBy: MembersSortBy, direction: MembersSortDirection): PersonType[] {
  const sorted = [...people];
  sorted.sort((a, b) => {
    const cmp = comparePeople(a, b, sortBy);
    return direction === 'desc' ? -cmp : cmp;
  });
  return sorted;
}
